import { formatDate, formatMoney, useLocale, useTranslation } from '@multaqa/i18n'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import { SkeletonTable } from '@multaqa/ui/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useMemo } from 'react'
import { InlineQueryError } from '@/components/inline-query-error'
import { TableScroll } from '@/components/table-scroll'
import { meQuery } from '@/lib/me'
import { useSupplierBills, useSupplierPayments } from '../hooks/use-supplier-accounting'

/** The supplier's bills: what we owe and when, newest first. */
export function SupplierBillsPanel({ supplierId }: { supplierId: string }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { data: me } = useQuery(meQuery)
  const bills = useSupplierBills(me?.active_company_id ?? null, supplierId)

  if (!me?.active_company_id) return null
  const rows = bills.data ?? []
  const state = panelState({
    panel: 'bills',
    loading: bills.isLoading,
    error: bills.isError,
    empty: rows.length === 0,
    onRetry: () => void bills.refetch(),
    t,
  })
  if (state) return state

  return (
    <TableScroll>
      <Table data-testid="supplier-bills-table">
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            <TableHead>{t('supplier.hub.columns.document')}</TableHead>
            <TableHead>{t('supplier.hub.columns.reference')}</TableHead>
            <TableHead>{t('supplier.hub.columns.date')}</TableHead>
            <TableHead>{t('supplier.hub.columns.dueDate')}</TableHead>
            <TableHead className="text-end">{t('supplier.hub.columns.amountDue')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((bill) => (
            <TableRow key={bill.id} data-testid={`supplier-bill-row-${bill.id}`}>
              <TableCell>
                <Link
                  to="/accounting/bills/$billId"
                  params={{ billId: bill.id }}
                  className="tabular-nums hover:underline"
                >
                  {bill.document_number ? (
                    <bdi dir="ltr">{bill.document_number}</bdi>
                  ) : (
                    t('supplier.hub.draft')
                  )}
                </Link>
              </TableCell>
              <TableCell>
                {bill.supplier_reference ? <bdi dir="ltr">{bill.supplier_reference}</bdi> : '—'}
              </TableCell>
              <TableCell className="tabular-nums">
                {formatDate(new Date(bill.bill_date), locale)}
              </TableCell>
              <TableCell className="tabular-nums">
                {formatDate(new Date(bill.due_date), locale)}
              </TableCell>
              <TableCell className="text-end tabular-nums">
                {formatMoney(bill.amount_due, bill.currency, locale)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableScroll>
  )
}

/** Payments made to the supplier. */
export function SupplierPaymentsPanel({ supplierId }: { supplierId: string }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { data: me } = useQuery(meQuery)
  const payments = useSupplierPayments(me?.active_company_id ?? null, supplierId)
  const made = useMemo(
    () =>
      (payments.data ?? []).filter(
        (payment) =>
          payment.direction === 'outbound' &&
          payment.partner_type === 'supplier' &&
          payment.partner_id === supplierId,
      ),
    [supplierId, payments.data],
  )

  if (!me?.active_company_id) return null
  const state = panelState({
    panel: 'payments',
    loading: payments.isLoading,
    error: payments.isError,
    empty: made.length === 0,
    onRetry: () => void payments.refetch(),
    t,
  })
  if (state) return state

  return (
    <TableScroll>
      <Table data-testid="supplier-payments-table">
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            <TableHead>{t('supplier.hub.columns.document')}</TableHead>
            <TableHead>{t('supplier.hub.columns.date')}</TableHead>
            <TableHead>{t('supplier.hub.columns.reference')}</TableHead>
            <TableHead className="text-end">{t('supplier.hub.columns.amount')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {made.map((payment) => (
            <TableRow key={payment.id} data-testid={`supplier-payment-row-${payment.id}`}>
              <TableCell className="tabular-nums">
                <Link
                  to="/accounting/payments/$paymentId"
                  params={{ paymentId: payment.id }}
                  className="hover:underline"
                >
                  {payment.payment_number ? (
                    <bdi dir="ltr">{payment.payment_number}</bdi>
                  ) : (
                    t('supplier.hub.draft')
                  )}
                </Link>
              </TableCell>
              <TableCell className="tabular-nums">
                {formatDate(new Date(payment.payment_date), locale)}
              </TableCell>
              <TableCell dir="ltr" className="text-start">
                {payment.reference ?? '—'}
              </TableCell>
              <TableCell className="text-end tabular-nums">
                {formatMoney(payment.amount, payment.currency, locale)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableScroll>
  )
}

function panelState({
  panel,
  loading,
  error,
  empty,
  onRetry,
  t,
}: {
  panel: 'bills' | 'payments'
  loading: boolean
  error: boolean
  empty: boolean
  onRetry: () => void
  t: (key: string) => string
}) {
  if (loading) return <SkeletonTable columns={[3, 2, 2, 2]} rows={4} />
  if (error) {
    return (
      <InlineQueryError
        title={t('common:error.asyncTitle')}
        description={t(`supplier.hub.${panel}LoadError`)}
        retryLabel={t('action.retry')}
        onRetry={onRetry}
      />
    )
  }
  if (empty) {
    return (
      <EmptyState
        title={t(`supplier.hub.${panel}Empty`)}
        description={t(`supplier.hub.${panel}EmptyDescription`)}
      />
    )
  }
  return null
}
